/**
 * 房间 UI 与连接层之间的唯一缝。
 *
 * 房间内的组件**不自己造 transport**：建连、鉴权、重连全在串联层（见 §11.7 的接线顺序），
 * 这里只声明「房间 UI 需要连接层提供什么」。`WsTransport` 在结构上天然满足 `RoomLink`，
 * 串联层直接 `<RoomLinkProvider link={transport}>` 即可，不需要适配器。
 *
 * 默认实现是**全空操作**：这样单测与 storybook 里渲染任何房间组件都不会炸，
 * 也不会偷偷建连。真要发消息却没接 Provider 时，行为是「什么都没发生」而不是崩溃——
 * 在牌桌上，崩溃比丢一次点击严重得多。
 */
import { createContext, useContext } from 'react'
import type { ReactNode } from 'react'
import type { ClientMessage, ServerFrame, ServerMessage } from '@poker/shared'

import { createServerClock, type ServerClock } from '../transport/serverClock.js'
import type { Backoff } from '../transport/backoff.js'

export type SnapshotReason = 'gap' | 'desync' | 'manual'

export interface RoomLink {
  /** 返回 cid。房间 UI 不用它做幂等（幂等键是 `clientActionId`），只在调试时有用。 */
  send(msg: ClientMessage): number
  /** ★ 红线 #49：想要快照只能走这里（单飞 + 2s 节流） */
  requestSnapshotOnce(reason: SnapshotReason): void
  /** 用户点「立即重试」 */
  retryNow(): void
  /** 倒计时插值用的服务端时钟（红线 #48：结果直接写 DOM，不进 state） */
  readonly clock: ServerClock
  /** 重连退避状态，断线条用它显示第几次尝试 */
  readonly backoff?: Backoff
  /**
   * 订阅入站消息，返回取消订阅函数。
   *
   * 房间 UI 只用它做一件事：`roomSnapshot` 落地时开 resync（禁两帧动画，§11.6）。
   * **状态一律从 `useNetStore` 读**，绝不在这里自己攒一份 —— 那就是第二份真相。
   */
  onMessage?(cb: (m: ServerMessage, frame: ServerFrame) => void): () => void
  /**
   * 只推送真正应用了的消息（重复帧、缺口帧不推）。冒聊天气泡、被催震动这类
   * **一次性**事件订它；没有它的连接（旧实现、测试替身）退回 `onMessage`。
   */
  onApplied?(cb: (m: ServerMessage, frame: ServerFrame) => void): () => void
}

/** 没接 Provider 时的兜底：时钟退化成本地时钟（offset 0），发送全部丢弃。 */
function createNullLink(): RoomLink {
  const clock = createServerClock({ now: () => Date.now() })
  return {
    send: () => 0,
    requestSnapshotOnce: () => undefined,
    retryNow: () => undefined,
    clock,
  }
}

const NULL_LINK = createNullLink()

const RoomLinkContext = createContext<RoomLink>(NULL_LINK)

export interface RoomLinkProviderProps {
  readonly link: RoomLink
  readonly children?: ReactNode
}

export function RoomLinkProvider({ link, children }: RoomLinkProviderProps): ReactNode {
  return <RoomLinkContext.Provider value={link}>{children}</RoomLinkContext.Provider>
}

export function useRoomLink(): RoomLink {
  return useContext(RoomLinkContext)
}
